import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

let _currentUser = null;
const _listeners = new Set();

export function setCurrentUser(u) {
  _currentUser = u || null;
  for (const fn of _listeners) fn(_currentUser);
}
export function subscribeCurrentUser(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

let _refreshInFlight = null;

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const original = err?.config;
    // Silent refresh on 401 — try once per request, avoid loops.
    if (status === 401 && original && !original._retried
        && !original.url?.includes("/auth/refresh")
        && !original.url?.includes("/auth/login")) {
      original._retried = true;
      try {
        // Coalesce concurrent refresh attempts into one round-trip.
        if (!_refreshInFlight) {
          _refreshInFlight = api.post("/auth/refresh").finally(() => { _refreshInFlight = null; });
        }
        await _refreshInFlight;
        return api(original);
      } catch (refreshErr) {
        setCurrentUser(null);
        return Promise.reject(refreshErr);
      }
    }
    if (status === 401) setCurrentUser(null);
    return Promise.reject(err);
  }
);

export const auth = {
  me: () => _currentUser,
  isAuthed: () => !!_currentUser,
  set: (u) => setCurrentUser(u),
  logout: async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("[api] logout failed:", e?.message); }
    setCurrentUser(null);
  },
};

export const authApi = {
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data),
  forgotPassword: (email) => api.post("/auth/forgot-password", { email }).then((r) => r.data),
  resetPassword: (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }).then((r) => r.data),
};

export const watchlistApi = {
  list: () => api.get("/watchlist").then((r) => r.data),
  ids: () => api.get("/watchlist/ids").then((r) => r.data),
  star: (facilityId) => api.post(`/watchlist/${facilityId}`).then((r) => r.data),
  unstar: (facilityId) => api.delete(`/watchlist/${facilityId}`).then((r) => r.data),
};

export const facilitiesApi = {
  meta: () => api.get("/facilities/meta").then((r) => r.data),
  list: (params) => api.get("/facilities", { params }).then((r) => r.data),
  listIds: (params) => api.get("/facilities/ids", { params }).then((r) => r.data),
  exportXlsx: (params) => api.get("/facilities/export.xlsx", { params, responseType: "blob" }),
  get: (id) => api.get(`/facilities/${id}`).then((r) => r.data),
  create: (payload) => api.post("/facilities", payload).then((r) => r.data),
  alternatives: (id, limit = 5) => api.get(`/facilities/${id}/alternatives`, { params: { limit } }).then((r) => r.data),
  history: (id) => api.get(`/facilities/${id}/history`).then((r) => r.data),
  audit: (id) => api.get(`/facilities/${id}/audit`).then((r) => r.data),
  edits: (id) => api.get(`/facilities/${id}/edits`).then((r) => r.data),
  update: (id, patch) => api.patch(`/facilities/${id}`, patch).then((r) => r.data),
  suspend: (id, reason) => api.post(`/facilities/${id}/suspend`, { reason }).then((r) => r.data),
  reactivate: (id, reason) => api.post(`/facilities/${id}/reactivate`, { reason }).then((r) => r.data),
  bulkUpdate: (facility_ids, patch) =>
    api.post("/facilities/bulk-update", { facility_ids: facility_ids.map(String), patch }).then((r) => r.data),
  bulkStatus: (facility_ids, new_status, reason) =>
    api.post("/facilities/bulk-status", { facility_ids: facility_ids.map(String), new_status, reason }).then((r) => r.data),
  remove: (id, reason) => api.delete(`/facilities/${id}`, { params: reason ? { reason } : {} }).then((r) => r.data),
  bulkDelete: (facility_ids, reason) =>
    api.post("/facilities/bulk-delete", {
      facility_ids: facility_ids.map(String), confirm: "DELETE", reason: reason || null,
    }).then((r) => r.data),
};

export const usersApi = {
  list: (statusFilter, opts = {}) => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (opts.telegramOnly) params.telegram_only = true;
    return api.get("/users", { params }).then((r) => r.data);
  },
  create: (payload) => api.post("/users", payload).then((r) => r.data),
  update: (id, patch) => api.patch(`/users/${id}`, patch).then((r) => r.data),
  unbindTelegram: (id) => api.post(`/users/${id}/unbind-telegram`).then((r) => r.data),
  delete: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  telegramPermissionsTaxonomy: () => api.get("/users/telegram-permissions/taxonomy").then((r) => r.data),
};

export const webUsersApi = {
  list: () => api.get("/web-users").then((r) => r.data),
  create: (payload) => api.post("/web-users", payload).then((r) => r.data),
  update: (id, patch) => api.patch(`/web-users/${id}`, patch).then((r) => r.data),
  resetPassword: (id, new_password) =>
    api.post(`/web-users/${id}/reset-password`, { new_password: new_password || null }).then((r) => r.data),
  delete: (id) => api.delete(`/web-users/${id}`).then((r) => r.data),
  taxonomy: () => api.get("/hsp-permissions/taxonomy").then((r) => r.data),
  merge: (targetUserId, sourceUserId) =>
    api.post(`/web-users/${targetUserId}/merge/${sourceUserId}`).then((r) => r.data),
};

export const rolesApi = {
  list: () => api.get("/roles").then((r) => r.data),
  taxonomy: () => api.get("/roles/taxonomy").then((r) => r.data),
  create: (payload) => api.post("/roles", payload).then((r) => r.data),
  update: (key, patch) => api.patch(`/roles/${key}`, patch).then((r) => r.data),
  delete: (key, reassign_to) =>
    api.delete(`/roles/${key}`, { params: reassign_to ? { reassign_to } : {} }).then((r) => r.data),
};

export const auditApi = {
  list: (params = {}) => api.get("/audit", { params }).then((r) => r.data),
  summary: () => api.get("/audit/summary").then((r) => r.data),
};

export const importApi = {
  upload: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  history: () => api.get("/import/history").then((r) => r.data),
  templateUrl: () => `${process.env.REACT_APP_BACKEND_URL}/api/import/template.xlsx`,
  uploadLegacy: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/import/legacy-suspensions", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  previewReplace: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/import/facilities/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  executeReplace: (file, { mode, confirm, deleteMissing }) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("mode", mode); fd.append("confirm", confirm); fd.append("delete_missing", deleteMissing ? "true" : "false");
    return api.post("/import/facilities/replace", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  listBackups: () => api.get("/import/facilities/backups").then((r) => r.data),
  mapsLinksTemplateUrl: () => `${process.env.REACT_APP_BACKEND_URL}/api/import/facilities/maps-links-template.xlsx`,
  previewMapsLinks: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/import/facilities/maps-links/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  applyMapsLinks: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/import/facilities/maps-links", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
};

export const reportsApi = {
  networkStatus: (params) => api.get("/reports/network-status", { params }).then((r) => r.data),
  suspensionTracking: (params) => api.get("/reports/suspension-tracking", { params }).then((r) => r.data),
  trend: (params) => api.get("/reports/trend", { params }).then((r) => r.data),
  csvUrl: (params) => { const qs = new URLSearchParams(params || {}).toString(); return `${process.env.REACT_APP_BACKEND_URL}/api/reports/suspension-tracking.csv${qs ? "?" + qs : ""}`; },
};

export const notificationsApi = {
  list: (params) => api.get("/notifications", { params }).then((r) => r.data),
  create: (payload) => api.post("/notifications", payload).then((r) => r.data),
  update: (id, patch) => api.patch(`/notifications/${id}`, patch).then((r) => r.data),
};

export const bulkApi = { updateFacilities: (facility_ids, patch) => api.post("/facilities/bulk-update", { facility_ids, patch }).then((r) => r.data) };

export const aiBulkApi = {
  consult: ({ instruction, facility_ids, answers, session_id }) => api.post("/ai-bulk-edit/consult", { instruction, facility_ids, answers, session_id }).then((r) => r.data),
  apply: ({ patches, instruction }) => api.post("/ai-bulk-edit/apply", { patches, instruction }).then((r) => r.data),
  fillDistricts: ({ facility_ids, limit, only_missing } = {}) =>
    api.post("/ai-bulk-edit/fill-districts", { facility_ids: facility_ids || null, limit: limit || 200, only_missing: only_missing !== false }).then((r) => r.data),
};

export const plansApi = {
  getConfig: () => api.get("/plans/config").then((r) => r.data),
  putConfig: (plans) => api.put("/plans/config", { plans }).then((r) => r.data),
};

export async function fetchCompanyReportBlob(companyId) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies/${companyId}/report.pdf`, { credentials: "include" });
  if (!res.ok) throw new Error(String(res.status));
  return res.blob();
}

export const companiesApi = {
  list: (params) => api.get("/companies", { params }).then((r) => r.data),
  meta: () => api.get("/companies/meta").then((r) => r.data),
  get: (id) => api.get(`/companies/${id}`).then((r) => r.data),
  create: (payload) => api.post("/companies", payload).then((r) => r.data),
  facilities: (id, params) => api.get(`/companies/${id}/facilities`, { params }).then((r) => r.data),
  setOverrides: (id, extra_facility_ids, excluded_facility_ids) => api.put(`/companies/${id}/overrides`, { extra_facility_ids, excluded_facility_ids }).then((r) => r.data),
  remove: (id) => api.delete(`/companies/${id}`).then((r) => r.data),
  byFacility: (facilityId) => api.get(`/companies/by-facility/${facilityId}`).then((r) => r.data),
  reportLink: (id) => api.get(`/companies/${id}/report-link`).then((r) => r.data),
  reportPdfUrl: (id) => `${process.env.REACT_APP_BACKEND_URL}/api/companies/${id}/report.pdf`,
  reportPdfBlob: fetchCompanyReportBlob,
  upload: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/companies/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  previewReplace: (file) => { const fd = new FormData(); fd.append("file", file); return api.post("/companies/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data); },
  executeReplace: (file, { mode, confirm, deleteMissing }) => {
    const fd = new FormData(); fd.append("file", file); fd.append("mode", mode); fd.append("confirm", confirm); fd.append("delete_missing", deleteMissing ? "true" : "false");
    return api.post("/companies/replace", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
  },
  templateUrl: () => "/companies/template.xlsx",
};

export const telegramApi = {
  status: () => api.get("/telegram/status").then((r) => r.data),
  diagnose: (params) => api.get("/telegram/diagnose", { params }).then((r) => r.data),
};
